<div class="p-4 mx-auto grid gap-5 text-slate-900">

  <!-- ── Auswahl ──────────────────────────────────────────────────────────── -->
  <section class="rounded-2xl border border-slate-900/10 bg-white p-4 shadow-sm">
    <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
      <h2 class="m-0 text-[15px] font-bold">Price Debugger</h2>
      <span class="inline-flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
        <span class="rounded-full bg-slate-900/5 px-2.5 py-[3px]">GET <code>{{ seriesPath }}</code></span>
        <span class="rounded-full bg-slate-900/5 px-2.5 py-[3px]">GET <code>{{ exaaPath }}</code></span>
        <span class="rounded-full bg-slate-900/5 px-2.5 py-[3px]">POST <code>{{ epexPath }}</code></span>
      </span>
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <mat-form-field appearance="outline" class="min-w-[260px]" subscriptSizing="dynamic">
        <mat-label>Standort</mat-label>
        <mat-select [value]="selectedLocationId()" (valueChange)="setLocationId($event)">
          @for (loc of locationState.locations(); track loc.id) {
            <mat-option [value]="loc.id">{{ loc.name }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <div class="flex items-center gap-1">
        <button mat-icon-button type="button" (click)="shiftDay(-1)" aria-label="Vorheriger Tag">
          <mat-icon>chevron_left</mat-icon>
        </button>
        <input
          type="date"
          class="rounded-[10px] border border-slate-900/20 px-2.5 py-2 text-sm"
          [ngModel]="date()"
          (ngModelChange)="$event && date.set($event)"
        />
        <button mat-icon-button type="button" (click)="shiftDay(1)" aria-label="Nächster Tag">
          <mat-icon>chevron_right</mat-icon>
        </button>
        <div class="inline-flex gap-1" role="group" aria-label="Tag wählen">
          @if (dayPreset() === 'today') {
            <button mat-flat-button type="button" aria-pressed="true" (click)="selectDay('today')">Heute</button>
          } @else {
            <button mat-stroked-button type="button" aria-pressed="false" (click)="selectDay('today')">Heute</button>
          }
          @if (dayPreset() === 'tomorrow') {
            <button mat-flat-button type="button" aria-pressed="true" (click)="selectDay('tomorrow')">Morgen</button>
          } @else {
            <button mat-stroked-button type="button" aria-pressed="false" (click)="selectDay('tomorrow')">Morgen</button>
          }
        </div>
        <button mat-stroked-button type="button" (click)="reload()" [disabled]="loading()">
          <mat-icon>refresh</mat-icon>
          Neu laden
        </button>
      </div>

      @if (loading()) {
        <span class="text-xs text-slate-500">Lädt…</span>
      }
    </div>
  </section>

  <!-- ── Aktuelle Preise (Bezug | Einspeisung) ─────────────────────────────── -->
  <section class="grid gap-4 md:grid-cols-2">
    <ng-container *ngTemplateOutlet="currentPrice; context: { title: 'Bezugspreis aktuell', state: consumption() }" />
    <ng-container *ngTemplateOutlet="currentPrice; context: { title: 'Einspeisungspreis aktuell', state: feedIn() }" />
  </section>

  <!-- ── Quellen ──────────────────────────────────────────────────────────── -->
  <section class="grid gap-4 md:grid-cols-3">
    <ng-container *ngTemplateOutlet="sourceStatus; context: { title: 'ENTSO-E', hint: 'source=transparency-energy&resolution=15m', state: entsoe(), count: entsoe().data?.data?.length, unit: entsoe().data?.unit }" />
    <ng-container *ngTemplateOutlet="sourceStatus; context: { title: 'APG (EXAA)', hint: 'exaa-price-series', state: apg(), count: apg().data?.data?.length, unit: apg().data?.unit }" />
    <ng-container *ngTemplateOutlet="sourceStatus; context: { title: 'EPEX SPOT (eingefügt)', hint: epex().data ? 'Liefertag ' + epex().data?.deliveryDate : 'epex-price-series', state: epex(), count: epex().data?.data?.length, unit: epex().data?.unit }" />
  </section>

  <!-- ── EPEX-Tabelle einfügen ────────────────────────────────────────────── -->
  <section class="rounded-2xl border border-slate-900/10 bg-white p-4 shadow-sm text-[13px]">
    <div class="mb-1.5 font-semibold">
      EPEX SPOT Day-Ahead einfügen
      <span class="font-normal text-slate-500">
        – Tabelle auf epexspot.com (Market Results, AT, Day-Ahead) markieren, kopieren und hier einfügen.
        Sie wird als Liefertag {{ date() }} gelesen.
      </span>
    </div>
    <textarea
      class="h-32 w-full rounded-[10px] border border-slate-900/20 p-2 font-mono text-xs"
      placeholder="Hours&#10;00:00 - 00:15&#10;…&#10;Buy Volume (MWh)   Sell Volume (MWh)   Volume (MWh)   Price (€/MWh)&#10;606.2   79.5   606.2   205.01&#10;…"
      [ngModel]="epexText()"
      (ngModelChange)="epexText.set($event)"
    ></textarea>
    <div class="mt-2 flex flex-wrap items-center gap-3">
      <button mat-stroked-button type="button" (click)="parseEpex()" [disabled]="!epexText().trim() || epex().loading">
        <mat-icon>content_paste_go</mat-icon>
        Übernehmen
      </button>
      <button mat-stroked-button type="button" (click)="clearEpex()" [disabled]="!epexText() && !epex().data">Leeren</button>
      @if (epex().loading) {
        <span class="text-slate-500">Wird gelesen…</span>
      } @else if (epex().error) {
        <span class="text-red-900" role="alert">{{ epex().error }}</span>
      } @else if (epex().data) {
        @let parsed = epex().data!;
        <span class="text-emerald-800">
          {{ parsed.data?.length ?? 0 }} Zeitschritte übernommen (Liefertag {{ parsed.deliveryDate }}, {{ parsed.interval }} min) ·
          Baseload {{ parsed.baseload | number: '1.2-2' }} · Peakload {{ parsed.peakload | number: '1.2-2' }} {{ parsed.unit }}
        </span>
      }
      @if (epexOtherDay()) {
        <span class="text-red-900">Die eingefügte Tabelle gilt für {{ epex().data?.deliveryDate }}, nicht für {{ date() }}.</span>
      }
    </div>
  </section>

  <!-- ── Preise nebeneinander ─────────────────────────────────────────────── -->
  <section class="rounded-2xl border border-slate-900/10 bg-white p-4 shadow-sm">
    <div class="mb-1.5 flex flex-wrap items-center justify-between gap-2">
      <div class="text-[13px] font-semibold">
        Preise <span class="font-normal text-slate-500">({{ rows().length }} Zeitschritte, alle Werte in ct/kWh, Zeitstempel Europe/Vienna)</span>
      </div>
      <button mat-stroked-button type="button" (click)="exportCsv()" [disabled]="rows().length === 0">
        <mat-icon>download</mat-icon>
        CSV exportieren
      </button>
    </div>
    @if (rows().length === 0) {
      <div class="text-[13px] text-slate-500">Für den {{ date() }} liefert keine Quelle Preise.</div>
    } @else {
      <div class="scroll-panel">
        <table class="debug-table">
          <thead>
            <tr>
              <th>Zeit</th>
              <th>ENTSO-E<span class="field-name">(transparency-energy)</span></th>
              <th>APG EXAA<span class="field-name">(exaaAuctionPrice)</span></th>
              <th>APG MC<span class="field-name">(mcAuctionPrice)</span></th>
              <th>EPEX<span class="field-name">(price)</span></th>
              <th>Δ ENTSO-E − APG MC</th>
              <th>Δ ENTSO-E − EPEX</th>
              <th>Δ APG MC − EPEX</th>
            </tr>
          </thead>
          <tbody>
            @for (row of rows(); track row.time) {
              @let entsoeVsMc = divergent(row.entsoe, row.apgMc);
              @let entsoeVsEpex = divergent(row.entsoe, row.epex);
              @let mcVsEpex = divergent(row.apgMc, row.epex);
              <tr>
                <td class="font-mono">{{ timestamp(row.time) }}</td>
                <td [class.divergent]="entsoeVsMc || entsoeVsEpex">{{ row.entsoe | number: '1.3-3' }}</td>
                <td>{{ row.apgExaa | number: '1.3-3' }}</td>
                <td [class.divergent]="entsoeVsMc || mcVsEpex">{{ row.apgMc | number: '1.3-3' }}</td>
                <td [class.divergent]="entsoeVsEpex || mcVsEpex">{{ row.epex | number: '1.3-3' }}</td>
                <td [class.divergent]="entsoeVsMc">{{ diff(row.entsoe, row.apgMc) | number: '1.3-3' }}</td>
                <td [class.divergent]="entsoeVsEpex">{{ diff(row.entsoe, row.epex) | number: '1.3-3' }}</td>
                <td [class.divergent]="mcVsEpex">{{ diff(row.apgMc, row.epex) | number: '1.3-3' }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
      <div class="mt-1 text-[11px] text-slate-500">
        Hervorgehoben: ENTSO-E, APG MC-Auktion und EPEX (dieselbe gekoppelte Day-Ahead-Auktion) weichen um mehr als 0,01 ct/kWh voneinander ab.
      </div>
    }
  </section>
</div>

<ng-template #currentPrice let-title="title" let-state="state">
  <div class="rounded-2xl border border-slate-900/10 bg-white p-4 shadow-sm text-[13px]">
    <div class="mb-2 font-semibold">{{ title }}</div>
    @if (state.loading) {
      <span class="text-slate-500">Lädt…</span>
    } @else if (state.error) {
      <span class="text-red-900">{{ state.error }}</span>
    } @else if (state.data) {
      @let price = state.data;
      <div class="mb-2 text-lg font-bold">{{ price.valueCtPerKwh | number: '1.3-3' }} <span class="text-xs font-normal">ct/kWh</span></div>
      <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
        <dt class="text-slate-500">Tarif</dt><dd>{{ price.tariffName ?? '–' }}</dd>
        <dt class="text-slate-500">Zeit</dt><dd>{{ timestamp(price.timestamp) }}</dd>
        <dt class="text-slate-500">Rohwert</dt><dd>{{ price.value | number: '1.2-4' }} {{ price.unit }}</dd>
        <dt class="text-slate-500">Energie</dt><dd>{{ price.energyPriceCtPerKwh | number: '1.3-3' }}</dd>
        <dt class="text-slate-500">Netz</dt><dd>{{ price.gridPriceCtPerKwh | number: '1.3-3' }}</dd>
        <dt class="text-slate-500">Börse</dt><dd>{{ price.marketPriceCtPerKwh | number: '1.3-3' }} ({{ price.marketPriceBasis ?? '–' }}, {{ timestamp(price.marketPriceTimestamp) }})</dd>
        <dt class="text-slate-500">Börse Basis / Ø Stunde</dt><dd>{{ price.marketPriceBaseCtPerKwh | number: '1.3-3' }} / {{ price.marketPriceHourlyAverageCtPerKwh | number: '1.3-3' }}</dd>
        <dt class="text-slate-500">Aufschlag</dt><dd>{{ price.markupCtPerKwh | number: '1.3-3' }}</dd>
        <dt class="text-slate-500">Anpassung</dt><dd>{{ price.adjustmentCtPerKwh | number: '1.3-3' }} ct / {{ price.adjustmentPercent | number: '1.0-2' }} %</dd>
      </dl>
    }
  </div>
</ng-template>

<ng-template #sourceStatus let-title="title" let-hint="hint" let-state="state" let-count="count" let-unit="unit">
  <div class="rounded-2xl border p-4 shadow-sm text-[13px]"
       [class]="state.error ? 'border-red-600/40 bg-red-600/[0.07]' : 'border-slate-900/10 bg-white'">
    <div class="font-semibold">{{ title }} <span class="font-mono text-[11px] font-normal text-slate-500">{{ hint }}</span></div>
    @if (state.loading) {
      <span class="text-slate-500">Lädt…</span>
    } @else if (state.error) {
      <span class="text-red-900" role="alert">{{ state.error }}</span>
    } @else if (state.data) {
      <span class="text-slate-500">{{ count ?? 0 }} Einträge · Einheit {{ unit ?? '–' }} · Intervall {{ state.data.interval ?? '–' }} min</span>
    }
  </div>
</ng-template>
